import type { TranslationKey } from '@/i18n';
import type { AlertData } from '@/types/alert.types';
import { isNonNullable } from '@/utils/nullability-helpers';
import { resolveTranslationKey } from '@/utils/localization-helpers';
import { notify } from '@kyvg/vue3-notification';
import { createInjectionState } from '@vueuse/core';
import { hasInjectionContext, onUnmounted, ref, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';

const SUCCESS_DURATION = 5000;

interface UseErrorAlertOptions {
    alertMode?: boolean;
}

let _nextErrorAlertScopeId = 0;
let providedAlert: ReturnType<typeof createAlertState> | undefined;

function createAlertState() {
    const errorAlertIds = new Set<number>();
    let nextAutoErrorId = 0;

    const route = useRoute();

    function trackErrorAlert(id: number): void {
        errorAlertIds.add(id);
    }

    function untrackErrorAlert(id: number): void {
        errorAlertIds.delete(id);
    }

    function dismissErrorAlerts(): void {
        errorAlertIds.forEach((id) => notify.close(id));
        errorAlertIds.clear();
    }

    function allocateErrorAlertId(explicitId?: number): number {
        return explicitId ?? ++nextAutoErrorId;
    }

    function notifyErrorAlert(
        text: string | TranslationKey,
        title?: string | TranslationKey,
        id?: number,
        data?: AlertData,
    ): void {
        const resolvedText = resolveTranslationKey(text);
        const resolvedTitle = resolveTranslationKey(title);
        const alertId = allocateErrorAlertId(id);
        trackErrorAlert(alertId);
        notify({
            id: alertId,
            type: 'error',
            text: resolvedText,
            title: resolvedTitle,
            duration: -1,
            data,
        });
    }

    function notifySuccessAlert(
        text: string | TranslationKey,
        title?: string | TranslationKey,
        data?: AlertData,
    ): void {
        const resolvedText = resolveTranslationKey(text);
        const resolvedTitle = resolveTranslationKey(title);
        dismissErrorAlerts();
        notify({
            type: 'success',
            text: resolvedText,
            title: resolvedTitle,
            duration: SUCCESS_DURATION,
            data,
        });
    }

    function useErrorAlert<T extends string | TranslationKey = TranslationKey>(
        errorOverride?: Ref<T | undefined>,
        options: UseErrorAlertOptions = {},
    ) {
        const { alertMode = false } = options;
        const error = errorOverride ?? ref<T | undefined>(undefined);
        const showErrorAlert = ref(alertMode && isNonNullable(error.value));
        const id = ++_nextErrorAlertScopeId;

        watch(error, (newError) => {
            if (alertMode) {
                showErrorAlert.value = isNonNullable(newError);
            } else if (isNonNullable(newError)) {
                notify.close(id);
                untrackErrorAlert(id);
                notifyErrorAlert(newError as unknown as TranslationKey, undefined, id);
            } else {
                notify.close(id);
                untrackErrorAlert(id);
            }
        });

        onUnmounted(() => {
            if (!alertMode) {
                notify.close(id);
                untrackErrorAlert(id);
            }
        });

        return { error, showErrorAlert };
    }

    function useSuccessAlert() {
        return { notifySuccess: notifySuccessAlert };
    }

    watch(
        () => route.fullPath,
        () => dismissErrorAlerts(),
    );

    return {
        trackErrorAlert,
        untrackErrorAlert,
        dismissErrorAlerts,
        notifyErrorAlert,
        notifySuccessAlert,
        useErrorAlert,
        useSuccessAlert,
    };
}

const [provideAlertState, useAlertNullable] = createInjectionState(createAlertState);

export function provideAlert() {
    providedAlert = provideAlertState();
    return providedAlert;
}

export const useAlert = () => {
    const injectedAlert = hasInjectionContext() ? useAlertNullable() : undefined;
    const alert = injectedAlert ?? providedAlert;

    if (alert === undefined) {
        throw new Error('provideAlert must be called before useAlert');
    }

    return alert;
};

export function notifyErrorAlert(
    text: string | TranslationKey,
    title?: string | TranslationKey,
    id?: number,
    data?: AlertData,
): void {
    useAlert().notifyErrorAlert(text, title, id, data);
}

/** @deprecated Prefer `notifyErrorAlert` */
export const notifyErrorMessage = notifyErrorAlert;

export function useErrorAlert<T extends string | TranslationKey = TranslationKey>(
    errorOverride?: Ref<T | undefined>,
    options?: UseErrorAlertOptions,
) {
    return useAlert().useErrorAlert<T>(errorOverride, options);
}

export function useSuccessAlert() {
    return useAlert().useSuccessAlert();
}
